import {
  AspectRatio as ChakraAspectRatio,
  Box,
  Button,
  Center,
  Divider,
  Flex,
  HStack,
  Icon,
  IconButton,
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverTrigger,
  Spinner,
  Text,
  Tooltip,
  useClipboard,
  useDisclosure,
  VStack,
  ButtonGroup,
} from '@chakra-ui/react'

import { SubMenu, MenuDivider, Menu } from '@szhsin/react-menu'

import type { MenuInstance } from '@szhsin/react-menu'
import moment from 'moment'
import numeral from 'numeral'
import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AiOutlinePlusCircle } from 'react-icons/ai'
import { BsChevronDown } from 'react-icons/bs'
import { FiEdit, FiLink, FiMoreVertical } from 'react-icons/fi'
import { LuHistory } from 'react-icons/lu'
import {
  MdCenterFocusWeak,
  MdOutlineFileDownload,
  MdOutlineShare,
} from 'react-icons/md'
import { PiMagicWandBold } from 'react-icons/pi'
import { RiImageEditLine } from 'react-icons/ri'
import { NavLink, useHistory, useRouteMatch } from 'react-router-dom'
import styled from 'styled-components'
import useFetch, { CachePolicies } from 'use-http'
import { checkOperationPermission, PermissionsEnum } from '../../../../utils'
import { FeaturesEnum } from '../../../../utils/features'
import {
  downloadFileFromUrl,
  getFileURL,
  isEditableImage,
  mediaLibraryThumbnailTransformation,
} from '../../../../utils/mediaLibrary'
import {
  removeLeadingSlash,
  removeTrailingSlash,
} from '../../../../utils/urlHelpers'
import { ACLPermissionEnum } from '../../../constants'
import useGetFileUrlArguments from '../../../hooks/useGetFileUrlArguments'
import useToast from '../../../hooks/useToast'
import type { FileDetailsResponse,  } from '../../../interfaces/FileDetailsResponse'
import type { CopyMoveOperations, TagOperations, MLFileDetailRouteParams } from '../../../interfaces'
import { UserContext } from '../../../providers'
import { MediaLibraryActions, MLContext } from '../../../providers/MLProvider'
import { PublicLinksInfoContext } from '../../../providers/PublicLinksInfoProvider'
import CopyTooltip from '../../CopyTooltip'
import PermissionGuard from '../../PermissionGuard'
import RemoveBgModal from '../Details/RemoveBgModal'
import FilePreview from '../FilePreview'
import { BsThreeDotsVertical } from 'react-icons/bs'
import {
  RenameFileModal,
  CopyMoveDrawer,
  DeleteMediaModal,
  DownloadModal,
  ShareAssetModal,
  ApplySavedExtensionModal,
} from '../MediaOperations'
import PublishFilesModal from '../MediaOperations/PublishFilesModal'
import CopyEmbedURLModal from '../CopyEmbedURLModal'
import {
  AddToMediaCollection,
  RemoveAssetFromMultipleMediaCollections,
} from '../../MediaCollections'
import ImageEditor from '../ImageEditor'
import AutoTagsModal from '../Details/AutoTagsModal'
import AddTagsModal from '../Details/AddTagsModal'
import FileHistoryDrawer from '../MediaOperations/FileHistoryDrawer'
import PublicLinksModal from '../MediaOperations/PublicLinksModal'
import FileUpload from '../NewButton/FileUpload'
import PhotoEditor from '../PhotoEditor'
import CustomFocusAreaModal from './CustomFoucsAreaModal'
import DeleteFileVersionModal from './DeleteFileVersionModal'
import RestoreFileVersionModal from './RestoreFileVersionModal'
import { useContextMenuItems } from '../hooks/useContextMenuItems'
import MenuItemWithTooltip from '../ContextMenu/MenuItem'
import { colors } from '../../../../ui-kit'
import { useDetailLinkBase } from '../../../hooks/useDetailLinkBase'
import { useDebounce } from '../../../hooks/useDebounce'

const AspectRatio = styled(ChakraAspectRatio)`
  & > img,
  & > video {
    object-fit: contain !important;
  }
`

interface FileDetailHeaderToolbarProps {
  fileDetails: FileDetailsResponse
  loading: boolean
  setFileDetails: React.Dispatch<
    React.SetStateAction<FileDetailsResponse | undefined>
  >
  getFileDetails(): Promise<FileDetailsResponse>
  handleClose(): void
}

const FileDetailHeaderToolbar = ({
  fileDetails,
  loading,
  getFileDetails,
  setFileDetails,
  handleClose,
}: FileDetailHeaderToolbarProps): React.ReactElement => {
  const {
    isPublicLink,
    urlPrefix,
    clientInfo,
    dashboardServerURL: publicLinkDashboardServerURL,
  } = useContext(PublicLinksInfoContext)
  const [state, dispatch] = useContext(MLContext)
  const { user } = useContext(UserContext)
  const dashboardServerURL =
    user?.dashboardServerURL ?? publicLinkDashboardServerURL
  const history = useHistory()
  const { background } = useDetailLinkBase()

  const {
    params: { fileId, versionId },
  } = useRouteMatch<MLFileDetailRouteParams>()

  const { t } = useTranslation()
  const [openEmbedModal, handleEmbedModal] = useState(false)
  const [copyMoveOperation, setCopyMoveOperation] =
    useState<CopyMoveOperations>('copy')
  const [tagOperation, setTagOperation] = useState<TagOperations>('updateTags')

  const [isFileUploadModalOpen, openFileUploadModal] = useState(false)
  const toast = useToast()
  const { request } = useFetch<string>({ cachePolicy: CachePolicies.NO_CACHE })

  const {
    isOpen: isOpenRemoveBgModal,
    onOpen: onOpenRemoveBgModal,
    onClose: onCloseRemoveBgModal,
  } = useDisclosure()

  const goToFileVersion = (e: React.MouseEvent, pathname: string): void => {
    e.preventDefault()
    e.stopPropagation()
    history.replace({
      pathname,
      state: { background },
    })
  }

  const {
    isOpen: isOpenPhotoEditor,
    onOpen: onOpenPhotoEditor,
    onClose: onClosePhotoEditor,
  } = useDisclosure()

  const {
    isOpen: isOpenRestoreFileVersion,
    onOpen: onOpenRestoreFileVersion,
    onClose: onCloseRestoreFileVersion,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteFileVersion,
    onOpen: onOpenDeleteFileVersion,
    onClose: onCloseDeleteFileVersion,
  } = useDisclosure()

  const {
    isOpen: isOpenHistoryDrawer,
    onOpen: onOpenHistoryDrawer,
    onClose: onCloseHistoryDrawer,
  } = useDisclosure()

  const {
    isOpen: isOpenRenameFileModal,
    onOpen: onOpenRenameFileModal,
    onClose: onCloseRenameFileModal,
  } = useDisclosure()

  const {
    isOpen: isOpenAddToMediaCollection,
    onOpen: onOpenAddToMediaCollection,
    onClose: onCloseAddToMediaCollection,
  } = useDisclosure()

  const {
    isOpen: isOpenPublishFilesModal,
    onOpen: onOpenPublishFilesModal,
    onClose: onClosePublishFilesModal,
  } = useDisclosure()

  const {
    isOpen: isOpenTagsModal,
    onOpen: onOpenTagsModal,
    onClose: onCloseTagsModal,
  } = useDisclosure()

  const {
    isOpen: isOpenAutoTagsModal,
    onOpen: onOpenAutoTagsModal,
    onClose: onCloseAutoTagsModal,
  } = useDisclosure()

  const {
    isOpen: isOpenRemoveAssetFromMultipleMediaCollections,
    onOpen: onOpenRemoveAssetFromMultipleMediaCollections,
    onClose: onCloseRemoveAssetFromMultipleMediaCollections,
  } = useDisclosure()

  const {
    isOpen: isOpenCustomFocusAreaModal,
    onOpen: onOpenCustomFocusAreaModal,
    onClose: onCloseCustomFocusAreaModal,
  } = useDisclosure()

  const {
    isOpen: isOpenCopyMoveDrawer,
    onOpen: onOpenCopyMoveDrawer,
    onClose: onCloseCopyMoveDrawer,
  } = useDisclosure()

  const {
    isOpen: isOpenPublicLinksModal,
    onOpen: onOpenPublicLinksModal,
    onClose: onClosePublicLinkModal,
  } = useDisclosure()

  const {
    isOpen: isOpenShareAssetModal,
    onOpen: onOpenShareAssetModal,
    onClose: onCloseShareAssetModal,
  } = useDisclosure()

  const {
    isOpen: isOpenDeleteMediaModal,
    onOpen: onOpenDeleteMediaModal,
    onClose: onCloseDeleteMediaModal,
  } = useDisclosure()

  const {
    isOpen: isOpenImageEditor,
    onOpen: onOpenImageEditor,
    onClose: onCloseImageEditor,
  } = useDisclosure()

  const {
    isOpen: isOpenDownloadModal,
    onOpen: onOpenDownloadModal,
    onClose: onCloseDownloadModal,
  } = useDisclosure()

  const {
    isOpen: isOpenApplySavedExtensionModal,
    onOpen: onOpenApplySavedExtensionModal,
    onClose: onCloseApplySavedExtensionModal,
  } = useDisclosure()

  const {
    data: versions,
    get: refetchFileVersions,
    loading: loadingVersions,
  } = useFetch<FileDetailsResponse[]>(
    `/api/v2/files/${fileDetails.fileId}/versions`,
    { method: 'GET', data: [], cachePolicy: CachePolicies.NO_CACHE },
  )

  useEffect(() => {
    if (!isPublicLink && Array.isArray(versions)) {
      dispatch({
        type: MediaLibraryActions.SET_DETAIL_PAGE_VERSIONS,
        payload: versions,
      })
    }
  }, [versions])

  const debouncedFileId = useDebounce(fileDetails.fileId, 500)

  useEffect(() => {
    if (!isPublicLink) {
      void refetchFileVersions()
    }
  }, [debouncedFileId, isPublicLink])

  const files = useMemo(
    () => versions?.filter(x => x.type === 'file'),
    [versions],
  )

  const fileVersions = useMemo(
    () => versions?.filter(x => x.type === 'file-version'),
    [versions],
  )

  const getFileUrlDynamicArguments = useGetFileUrlArguments({
    clientInfo,
    user,
  })

  const buildPublicUrl = async (): Promise<string> => {
    const data = await getFileURL({
      file: fileDetails,
      request,
      ...getFileUrlDynamicArguments,
    })
    if (data.includes('ik-s=')) {
      if (data.includes('ik-auth-version')) {
        const url = new URL(data)
        url.searchParams.delete('ik-auth-version')
        url.searchParams.delete('ik-s')
        url.searchParams.delete('ik-t')
        return url.toString()
      }
      return data
    }
    return data
  }
  const refMap = {
    organize: useRef<MenuInstance>(null),
    edit: useRef<MenuInstance>(null),
    tags: useRef<MenuInstance>(null),
    download: useRef<MenuInstance>(null),
    share: useRef<MenuInstance>(null),
  }

  const contextMenuItemList = useContextMenuItems({
    setCopyMoveOperation,
    setTagOperation,
    onOpenCopyMoveDrawer,
    onOpenRenameFileModal,
    onOpenRenameFolderModal: () => {},
    onOpenRenameMediaCollectionModal: () => {},
    onOpenPublishFilesModal,
    onOpenTagsModal,
    onOpenAutoTagsModal,
    onOpenRemoveAssetFromMultipleMediaCollections,
    onOpenRemoveMultipleAssetsFromMediaCollection: () => {},
    onOpenDeleteMediaModal,
    onOpenDeleteFileVersionModal: onOpenDeleteFileVersion,
    onOpenRestoreFileVersionModal: onOpenRestoreFileVersion,
    onOpenRemoveBgModal,
    onOpenCustomFocusAreaModal,
    onOpenShareAssetModal,
    onOpenShareMediaCollectionModal: () => {},
    onOpenPublicLinksModal,
    onOpenAddToMediaCollection,
    onOpenDownloadModal,
    handleEmbedModal,
    onOpenApplyPathPolicyModal: () => {},
    onOpenApplySavedExtensionModal,
    onOpenImageEditor,
    onOpenPhotoEditor,
    refMap,
    isDetailModel: true,
    file: {
      // Convert FileDetailsResponse to MediaLibrarySearchFileResponse format
      type: fileDetails?.type,
      name: fileDetails?.name,
      versionInfo: {
        id: fileDetails?.versionInfo?.id,
        name: fileDetails?.versionInfo?.name,
      },
      createdAt: fileDetails?.createdAt,
      updatedAt: fileDetails?.updatedAt,
      fileId: fileDetails?.fileId,
      embeddedMetadata: null, // FileDetailsResponse has rich metadata, SearchResponse expects null
      tags: fileDetails?.tags,
      AITags: fileDetails?.AITags
        ? fileDetails?.AITags.map(tag => ({
            name: tag?.name,
            confidence: tag?.confidence,
            source: tag?.source,
          }))
        : null,
      customCoordinates: fileDetails?.customCoordinates,
      customMetadata: fileDetails?.customMetadata || {},
      isPrivateFile: fileDetails?.isPrivateFile,
      isPublished: fileDetails?.isPublished,
      url: fileDetails?.url,
      thumbnail: fileDetails?.thumbnail,
      fileType: fileDetails?.fileType === 'image' ? 'image' : 'non-image',
      filePath: fileDetails?.filePath,
      height: fileDetails?.height,
      width: fileDetails?.width,
      size: fileDetails?.size,
      hasAlpha: fileDetails?.hasAlpha,
      mime: fileDetails?.mime || '',
      permission: fileDetails?.permission,
      description: fileDetails?.description,
      createdBy: fileDetails?.createdBy,
    },
  })

  const historyDisabled = (() => {
    if (
      !user?.pricingPlan.featuresList.includes(FeaturesEnum.CENTRAL_AUDIT_LOGS)
    ) {
      return {
        isDisabled: true,
        tooltipMessage: t(
          'history_not_available',
          'History is not available in your current plan. Please upgrade to access this feature.',
        ),
      }
    }

    return {
      isDisabled: false,
    }
  })()

  const onPatched = (patch: Partial<FileDetailsResponse>): void => {
    setFileDetails(prev => ({ ...(prev as FileDetailsResponse), ...patch }))
  }

  return (
    <>
      <Flex
        width="100%"
        alignItems="center"
        height="100%"
        lineHeight={1.5}
        justifyContent="space-between"
      >
        <Flex
          alignItems="flex-start"
          justifyContent="center"
          direction="column"
        >
          <Flex alignItems="center">
            <Box maxWidth="18em">
              <Tooltip label={fileDetails.name}>
                <Text isTruncated fontWeight="600">
                  {fileDetails.name}
                </Text>
              </Tooltip>
            </Box>

            {!isPublicLink && (
              <PermissionGuard
                permission={
                  PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                }
              >
                {(hasPermission: boolean) =>
                  hasPermission ? (
                    <Tooltip 
                      label={t('rename_file', 'Rename file')}
                      isDisabled={isOpenImageEditor}
                    >
                      <IconButton
                        aria-label={t('rename_file', 'Rename file')}
                        variant="link"
                        color="#1a202c"
                        icon={<FiEdit />}
                        minW="20px"
                        ml="12px"
                        onClick={onOpenRenameFileModal}
                      />
                    </Tooltip>
                  ) : (
                    <></>
                  )
                }
              </PermissionGuard>
            )}

            <CopyTooltip
              label={t('copy_url', 'Copy URL')}
              successLabel={t('copied2', 'Copied')}
              placement="top"
              getCopyValue={buildPublicUrl}
              isDisabled={isOpenImageEditor}
              onError={() =>
                toast({
                  description: t(
                    'copy_failed_try_again',
                    'Copy failed. Try again.',
                  ),
                  status: 'error',
                  isClosable: true,
                  duration: 5000,
                  position: 'top-right',
                  variant: 'outline',
                })
              }
            >
              <IconButton
                aria-label={t('copy_url', 'Copy URL')}
                variant="link"
                color="#1a202c"
                icon={<FiLink />}
                minW="20px"
                ml="6px"
              />
            </CopyTooltip>
          </Flex>

          {!isPublicLink ? (
            <Flex mt="6px" align="center">
              <Box maxWidth="20em">
                <Popover placement="bottom-start">
                  <PopoverTrigger>
                    <Button
                      variant="link"
                      fontWeight="500"
                      fontSize="12px"
                      borderRadius="16px"
                      p="3px 8px"
                      color="appDarkIndigo"
                      border="1px solid #e1e4eb"
                      rightIcon={<BsChevronDown />}
                      isLoading={loadingVersions}
                    >
                      {fileDetails.type === 'file'
                        ? 'Latest version'
                        : fileDetails?.versionInfo?.name || 'Version 1'}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    borderRadius="4px"
                    padding="0px"
                    _focus={{ boxShadow: 'none' }}
                  >
                    <PopoverArrow />
                    <PopoverBody padding="0px">
                      {loadingVersions ? (
                        <Center>
                          <Spinner />
                        </Center>
                      ) : (
                        <VStack
                          justifyContent="flex-start"
                          alignItems="flex-start"
                          width="full"
                          spacing="2"
                          paddingX="4"
                          paddingY="4"
                          overflow="scroll"
                          maxHeight="320px"
                        >
                          {files?.map(version => {
                            const isCurrentVersion =
                              version.type === 'file' && !versionId
                            return (
                              <Flex
                                key={version.versionInfo.id}
                                direction="column"
                                width="full"
                                as="a"
                                onClick={e =>
                                  void goToFileVersion(
                                    e,
                                    `/media-library/detail/${version.fileId}`,
                                  )
                                }
                                borderRadius="md"
                                padding="2"
                                outline="1px solid"
                                background={
                                  isCurrentVersion ? 'transparent' : '#f7fafc'
                                }
                                outlineColor={
                                  isCurrentVersion ? 'appClearBlue' : '#e1e4eb'
                                }
                              >
                                <Flex align="center" justify="space-between">
                                  <Flex direction="column">
                                    <Text
                                      fontWeight="semibold"
                                      color={
                                        isCurrentVersion
                                          ? 'appClearBlue'
                                          : 'black'
                                      }
                                      isTruncated
                                    >
                                      Current version
                                    </Text>
                                    <Text color="appSlate" fontSize="sm">
                                      <Tooltip
                                        label={moment(
                                          version.updatedAt,
                                        ).toLocaleString()}
                                        portalProps={{
                                          appendToParentPortal: true,
                                        }}
                                      >
                                        {moment(version.updatedAt).fromNow()}
                                      </Tooltip>
                                      {' • '}
                                      {numeral(version.size)
                                        .format('0.00 ib')
                                        .replace('i', '')}
                                    </Text>
                                  </Flex>
                                  <AspectRatio width="30%" ratio={1 / 1}>
                                    <FilePreview
                                      file={version}
                                      folderPath={version.filePath}
                                      transformation={
                                        mediaLibraryThumbnailTransformation
                                      }
                                      disablePadding
                                    />
                                  </AspectRatio>
                                </Flex>
                              </Flex>
                            )
                          })}
                          {fileVersions?.map((version, index) => {
                            const isCurrentVersion =
                              version.versionInfo.id === versionId
                            const isNextCurrentVersion =
                              fileVersions[index + 1]?.versionInfo.id ===
                              versionId
                            const currentVersionStyles = isCurrentVersion
                              ? {
                                  outline: '1px solid',
                                  outlineColor: 'appClearBlue',
                                  padding: '2',
                                }
                              : {
                                  paddingX: '2',
                                  outline: '1px solid',
                                  outlineColor: 'transparent',
                                  paddingTop: index === 0 ? '2' : '0',
                                }
                            return (
                              <Flex
                                key={version.versionInfo.id}
                                direction="column"
                                width="full"
                                as="a"
                                onClick={e =>
                                  void goToFileVersion(
                                    e,
                                    `/media-library/detail/${version.fileId}/${version.versionInfo.id}`,
                                  )
                                }
                                borderRadius="md"
                                {...currentVersionStyles}
                              >
                                <Flex align="center" justify="space-between">
                                  <Flex direction="column">
                                    <Text
                                      fontWeight="semibold"
                                      color={
                                        isCurrentVersion
                                          ? 'appClearBlue'
                                          : 'black'
                                      }
                                      isTruncated
                                    >
                                      {version.versionInfo.name}
                                    </Text>
                                    <Text color="appSlate" fontSize="sm">
                                      <Tooltip
                                        label={moment(
                                          version.createdAt,
                                        ).toLocaleString()}
                                        portalProps={{
                                          appendToParentPortal: true,
                                        }}
                                      >
                                        {moment(version.createdAt).fromNow()}
                                      </Tooltip>
                                      {' • '}
                                      {numeral(version.size)
                                        .format('0.00 ib')
                                        .replace('i', '')}
                                    </Text>
                                  </Flex>
                                  <AspectRatio width="30%" ratio={1 / 1}>
                                    <FilePreview
                                      file={version}
                                      folderPath={version.filePath}
                                      transformation={
                                        mediaLibraryThumbnailTransformation
                                      }
                                      disablePadding
                                    />
                                  </AspectRatio>
                                </Flex>
                                <Divider
                                  borderColor={
                                    !isCurrentVersion &&
                                    !isNextCurrentVersion &&
                                    index !== fileVersions.length - 1
                                      ? 'inherit'
                                      : 'transparent'
                                  }
                                  marginTop={
                                    !isCurrentVersion &&
                                    !isNextCurrentVersion &&
                                    index !== fileVersions.length - 1
                                      ? '2'
                                      : '0'
                                  }
                                />
                              </Flex>
                            )
                          })}
                        </VStack>
                      )}
                    </PopoverBody>
                  </PopoverContent>
                </Popover>
              </Box>
              {checkOperationPermission({
                user,
                aclPermission: [
                  ACLPermissionEnum.CONTRIBUTE,
                  ACLPermissionEnum.MANAGE,
                ],
                selectedFiles: [fileDetails],
                selectedFolders: [],
                selectedMediaCollections: [],
              }) && (
                <PermissionGuard
                  permission={
                    PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE
                  }
                >
                  {(hasPermission: boolean) =>
                    hasPermission ? (
                      <Tooltip
                        label={t(
                          'upload_new_file_version',
                          'Upload new file version',
                        )}
                        isDisabled={isOpenImageEditor}
                        shouldWrapChildren
                      >
                        <Icon
                          as={AiOutlinePlusCircle}
                          cursor="pointer"
                          mt="3px"
                          ml="12px"
                          boxSize="16px"
                          onClick={() => openFileUploadModal(true)}
                        />
                      </Tooltip>
                    ) : (
                      <></>
                    )
                  }
                </PermissionGuard>
              )}
            </Flex>
          ) : null}
        </Flex>
        <Box>
          <PermissionGuard
            permission={PermissionsEnum.RESTRICTED_SELF_MEDIA_LIBRARY_MANAGE}
          >
            {(hasPermission: boolean) =>
              hasPermission && fileDetails && !loading ? (
                <HStack spacing="4px">
                  <HStack gap="16px">
                    {contextMenuItemList
                      .filter(x => x.name === 'Share' && x.viewConditions)[0]
                      ?.subMenu?.items?.filter(
                        x => x.name === 'Invite+' && x.viewConditions,
                      )
                      .map(x => {
                        const { isDisabled, tooltipMessage } =
                          x?.optionDisabledInfo
                            ? x.optionDisabledInfo()
                            : { isDisabled: false, tooltipMessage: '' }
                        return (
                          <Button
                            variant={'variantUnstyled'}
                            padding={'0px'}
                            margin={'0px'}
                            fontWeight={400}
                            onClick={x.trigger}
                            disabled={isDisabled}
                            cursor={'pointer'}
                            color="#1a202c"
                          >
                            <Tooltip
                              label={tooltipMessage}
                              isDisabled={!isDisabled || isOpenImageEditor}
                            >
                              <HStack spacing="4px">
                                <Icon as={x.icon} boxSize={4} />
                                <Text>{'Share'}</Text>
                              </HStack>
                            </Tooltip>
                          </Button>
                        )
                      })}
                    {contextMenuItemList
                      .filter(x => x.viewConditions && x.name === 'Download')
                      .map((downloadItem, ind) => (
                        <Menu
                          menuButton={
                            <Button
                              variant={'variantUnstyled'}
                              padding={'0px'}
                              margin={'0px'}
                              fontWeight={400}
                              color="#1a202c"
                            >
                              <HStack spacing="4px">
                                <Icon as={downloadItem.icon} boxSize={4} />
                                <Text>{'Download'}</Text>
                              </HStack>
                            </Button>
                          }
                          submenuCloseDelay={0}
                          submenuOpenDelay={0}
                          menuStyle={{
                            padding: '4px 0px',
                            borderRadius: 8,
                            boxShadow: 'none',
                            border: `1px solid ${colors.appLightGreyTwo}`,
                            minWidth: 300,
                          }}
                        >
                          {downloadItem?.subMenu?.items
                            ?.filter(item => item.viewConditions)
                            .map((item, itemInd) => {
                              if (item.name === 'divider') {
                                return (
                                  <MenuDivider
                                    key={`submenu-divider-${ind}-${itemInd}`}
                                  />
                                )
                              }
                              return (
                                <MenuItemWithTooltip
                                  item={item}
                                  ind={ind}
                                  itemInd={itemInd}
                                />
                              )
                            })}
                        </Menu>
                      ))}
                    {contextMenuItemList
                      .filter(x => x.name === 'Edit' && x.viewConditions)[0]
                      ?.subMenu?.items?.filter(
                        x => x.name === 'Remove background' && x.viewConditions,
                      )
                      .map(x => {
                        const { isDisabled, tooltipMessage } =
                          x?.optionDisabledInfo
                            ? x.optionDisabledInfo()
                            : { isDisabled: false, tooltipMessage: '' }
                        return (
                          <Button
                            variant={'variantUnstyled'}
                            padding={'0px'}
                            margin={'0px'}
                            fontWeight={400}
                            onClick={x.trigger}
                            disabled={isDisabled}
                            cursor={'pointer'}
                            color="#1a202c"
                          >
                            <Tooltip
                              label={tooltipMessage}
                              isDisabled={!isDisabled || isOpenImageEditor}
                            >
                              <HStack spacing="4px">
                                <Icon as={x.icon} boxSize={4} />
                                <Text>{'Remove background'}</Text>
                              </HStack>
                            </Tooltip>
                          </Button>
                        )
                      })}
                    {fileDetails.type === 'file' && !isPublicLink && (
                      <Button
                        variant={'variantUnstyled'}
                        padding={'0px'}
                        margin={'0px'}
                        fontWeight={400}
                        disabled={historyDisabled?.isDisabled}
                        onClick={onOpenHistoryDrawer}
                        cursor={'pointer'}
                        color="#1a202c"
                      >
                        <Tooltip
                          label={historyDisabled?.tooltipMessage}
                          isDisabled={!historyDisabled?.isDisabled || isOpenImageEditor}
                        >
                          <HStack spacing="4px">
                            <Icon as={LuHistory} boxSize={4} />
                            <Text>{'History'}</Text>
                          </HStack>
                        </Tooltip>
                      </Button>
                    )}

                    {fileDetails.type === 'file-version' &&
                      contextMenuItemList
                        .filter(
                          x => x.name === 'Restore version' && x.viewConditions,
                        )
                        .map(x => {
                          const { isDisabled, tooltipMessage } =
                            x?.optionDisabledInfo
                              ? x.optionDisabledInfo()
                              : { isDisabled: false, tooltipMessage: '' }
                          return (
                            <Button
                              variant={'variantUnstyled'}
                              padding={'0px'}
                              margin={'0px'}
                              fontWeight={400}
                              onClick={x.trigger}
                              disabled={isDisabled}
                              cursor={'pointer'}
                              color="#1a202c"
                            >
                              <Tooltip
                                label={tooltipMessage}
                                isDisabled={!isDisabled || isOpenImageEditor}
                              >
                                <HStack spacing="4px">
                                  <Icon as={x.icon} boxSize={4} />
                                  <Text>{'Restore version'}</Text>
                                </HStack>
                              </Tooltip>
                            </Button>
                          )
                        })}

                    {fileDetails.type === 'file-version' &&
                      contextMenuItemList
                        .filter(
                          x => x.name === 'Delete version' && x.viewConditions,
                        )
                        .map(x => {
                          const { isDisabled, tooltipMessage } =
                            x?.optionDisabledInfo
                              ? x.optionDisabledInfo()
                              : { isDisabled: false, tooltipMessage: '' }
                          return (
                            <Button
                              variant={'variantUnstyled'}
                              padding={'0px'}
                              margin={'0px'}
                              fontWeight={400}
                              onClick={x.trigger}
                              disabled={isDisabled}
                              cursor={'pointer'}
                              color={colors.appBrick}
                            >
                              <Tooltip
                                label={tooltipMessage}
                                isDisabled={!isDisabled || isOpenImageEditor}
                              >
                                <HStack spacing="4px">
                                  <Icon as={x.icon} boxSize={4} />
                                  <Text>{'Delete version'}</Text>
                                </HStack>
                              </Tooltip>
                            </Button>
                          )
                        })}

                    {fileDetails.type === 'file' && (
                      <Menu
                        menuButton={
                          <Box>
                            <Tooltip
                              label={t('more_options', 'More Options')}
                              shouldWrapChildren
                              isDisabled={isOpenImageEditor}
                            >
                              <IconButton
                                minHeight="unset"
                                minWidth="unset"
                                padding="0px"
                                margin="0px"
                                height="17px"
                                aria-label="More Options"
                                icon={
                                  <Icon as={BsThreeDotsVertical} boxSize={4} />
                                }
                                variant="variantUnstyled"
                                color="#1a202c"
                              />
                            </Tooltip>
                          </Box>
                        }
                        submenuCloseDelay={0}
                        submenuOpenDelay={0}
                        menuStyle={{
                          padding: '4px 0px',
                          borderRadius: 8,
                          boxShadow: 'none',
                          border: `1px solid ${colors.appLightGreyTwo}`,
                          minWidth: 200,
                          zIndex: 1000,
                        }}
                        onMouseLeave={() => {
                          Object.values(refMap).forEach(ref =>
                            ref?.current?.closeMenu(),
                          )
                        }}
                      >
                        {contextMenuItemList
                          .filter(
                            x =>
                              x.viewConditions &&
                              !['Apply path policy'].includes(x.name),
                          )
                          .map((x, ind) => {
                            if (x.subMenu) {
                              const filteredItems = x.subMenu.items?.filter(
                                item => item.viewConditions,
                              )

                              if (filteredItems?.length === 1) {
                                return (
                                  <MenuItemWithTooltip
                                    item={filteredItems[0]}
                                    ind={ind}
                                    itemInd={0}
                                  />
                                )
                              }
                              return (
                                <SubMenu
                                  position="anchor"
                                  menuStyle={{
                                    padding: 4,
                                    borderRadius: 8,
                                    boxShadow: 'none',
                                    border: `1px solid ${colors.appLightGreyTwo}`,
                                    minWidth: x.subMenu.minWidth || undefined,
                                  }}
                                  instanceRef={x.reference}
                                  label={x.element?.({})}
                                  gap={0}
                                  key={`submenu-${ind}`}
                                >
                                  {filteredItems.map((item, itemInd) => {
                                    if (item.name === 'divider') {
                                      return (
                                        <MenuDivider
                                          key={`submenu-divider-${ind}-${itemInd}`}
                                        />
                                      )
                                    }
                                    return (
                                      <MenuItemWithTooltip
                                        item={item}
                                        ind={ind}
                                        itemInd={itemInd}
                                      />
                                    )
                                  })}
                                </SubMenu>
                              )
                            } else if (x.name === 'divider') {
                              return <MenuDivider key={`divider-${ind}`} />
                            } else {
                              return <MenuItemWithTooltip item={x} ind={ind} />
                            }
                          })}
                      </Menu>
                    )}
                  </HStack>
                </HStack>
              ) : (
                <></>
              )
            }
          </PermissionGuard>
        </Box>
      </Flex>

      {isOpenDownloadModal && (
        <DownloadModal
          fileDetails={fileDetails}
          isOpen={isOpenDownloadModal}
          onClose={onCloseDownloadModal}
        />
      )}

      {isOpenImageEditor && (
        <ImageEditor onClose={onCloseImageEditor} fileDetails={fileDetails} />
      )}

      {isOpenDeleteMediaModal && (
        <DeleteMediaModal
          fileDetails={fileDetails}
          isOpen={isOpenDeleteMediaModal}
          onClose={() => {
            onCloseDeleteMediaModal()
            handleClose()
          }}
        />
      )}
      {isOpenPublishFilesModal && (
        <PublishFilesModal
          fileDetails={fileDetails}
          isOpen={isOpenPublishFilesModal}
          onClose={onClosePublishFilesModal}
        />
      )}

      {isOpenShareAssetModal && (
        <ShareAssetModal
          isOpen={isOpenShareAssetModal}
          onClose={onCloseShareAssetModal}
          assetId={fileDetails?.fileId ?? ''}
          assetName={fileDetails?.name ?? ''}
          assetType={fileDetails?.type ?? ''}
        />
      )}
      {isOpenPublicLinksModal && (
        <PublicLinksModal
          assetId={fileDetails.fileId}
          assetType={fileDetails.type}
          assetName={fileDetails.name}
          isOpen={isOpenPublicLinksModal}
          onClose={onClosePublicLinkModal}
        />
      )}
      {isOpenCopyMoveDrawer && (
        <CopyMoveDrawer
          fileDetails={fileDetails}
          operation={copyMoveOperation}
          isOpen={isOpenCopyMoveDrawer}
          onClose={() => {
            onCloseCopyMoveDrawer()
            if (copyMoveOperation === 'move') {
              handleClose()
            }
          }}
        />
      )}

      {openEmbedModal ? (
        <CopyEmbedURLModal
          file={fileDetails}
          openEmbedModal={openEmbedModal}
          handleEmbedModal={handleEmbedModal}
        />
      ) : null}

      {isOpenRemoveAssetFromMultipleMediaCollections && (
        <RemoveAssetFromMultipleMediaCollections
          fileDetails={fileDetails}
          isOpen={isOpenRemoveAssetFromMultipleMediaCollections}
          onClose={onCloseRemoveAssetFromMultipleMediaCollections}
        />
      )}
      {isOpenTagsModal && (
        <AddTagsModal
          fileDetails={fileDetails}
          data={fileDetails.tags ?? []}
          isOpen={isOpenTagsModal}
          onClose={onCloseTagsModal}
          onPatchedTags={nextTags => {
            const updated = { ...fileDetails, tags: nextTags }
            dispatch({
              type: MediaLibraryActions.UPDATE_FILES,
              payload: [updated],
            })
            onPatched({ tags: nextTags })
          }}
        />
      )}

      {isOpenAutoTagsModal && (
        <AutoTagsModal
          fileId={fileDetails?.fileId}
          isOpen={isOpenAutoTagsModal}
          onClose={onCloseAutoTagsModal}
          onPatchedDetails={patch => {
            const updated = { ...fileDetails, ...patch }
            dispatch({
              type: MediaLibraryActions.UPDATE_FILES,
              payload: [updated],
            })
            setFileDetails(updated)
          }}
        />
      )}

      {isOpenAddToMediaCollection && (
        <AddToMediaCollection
          fileDetails={fileDetails}
          isOpen={isOpenAddToMediaCollection}
          onClose={onCloseAddToMediaCollection}
          onOpen={onOpenAddToMediaCollection}
        />
      )}
      {isEditableImage(fileDetails) && isOpenCustomFocusAreaModal && (
        <CustomFocusAreaModal
          isOpen={isOpenCustomFocusAreaModal}
          onClose={onCloseCustomFocusAreaModal}
          fileDetails={fileDetails}
        />
      )}
      {isEditableImage(fileDetails) && isOpenPhotoEditor && (
        <PhotoEditor
          onClose={async () => {
            onClosePhotoEditor()
            await refetchFileVersions()
          }}
          fileDetails={fileDetails}
          dispatch={dispatch}
        />
      )}

      {isOpenDeleteFileVersion && (
        <DeleteFileVersionModal
          onSuccessClose={async () => {
            await refetchFileVersions()
            history.replace({
              pathname: `/media-library/detail/${fileId}`,
              state: { background },
            })
          }}
          isOpen={isOpenDeleteFileVersion}
          onClose={onCloseDeleteFileVersion}
        />
      )}

      {isOpenRestoreFileVersion && (
        <RestoreFileVersionModal
          onSuccessClose={async () => {
            await refetchFileVersions()
            history.replace({
              pathname: `/media-library/detail/${fileId}`,
              state: { background },
            })
          }}
          isOpen={isOpenRestoreFileVersion}
          onClose={onCloseRestoreFileVersion}
        />
      )}
      <FileHistoryDrawer
        isOpen={isOpenHistoryDrawer}
        onClose={onCloseHistoryDrawer}
        fileDetails={fileDetails}
      />
      <FileUpload
        isModalOpen={isFileUploadModalOpen}
        openModal={() => openFileUploadModal(true)}
        closeModal={async (hasUploadedFiles: boolean) => {
          openFileUploadModal(false)
          if (hasUploadedFiles) {
            await refetchFileVersions()
            const details = await getFileDetails()
            setFileDetails(details)
          }
        }}
        fileVersion={fileDetails}
      />
      {isOpenRenameFileModal && (
        <RenameFileModal
          fileDetails={fileDetails}
          setFileDetails={setFileDetails}
          isOpen={isOpenRenameFileModal}
          onClose={onCloseRenameFileModal}
        />
      )}
      {isOpenRemoveBgModal && (
        <RemoveBgModal
          isOpen={isOpenRemoveBgModal}
          onClose={onCloseRemoveBgModal}
          fileId={fileDetails.fileId}
        />
      )}
      {isOpenApplySavedExtensionModal && (
        <ApplySavedExtensionModal
          fileDetails={fileDetails}
          setFileDetails={setFileDetails}
          isOpen={isOpenApplySavedExtensionModal}
          onClose={onCloseApplySavedExtensionModal}
        />
      )}
    </>
  )
}

export default FileDetailHeaderToolbar
